import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Auftrag, Dokumentenverweis } from '../../../../../api';
import { viewPDF } from '../../../../../redux/actions/auftrag';
import Button from '../../../../../components/button/Button';
import { StateModel } from '../../../../../redux/store';
import {
  deleteFile,
  loadDocument,
} from '../../../../../redux/actions/dokumentenverweis';
import { DokumentenModel } from '../../../../../models/DokumentenModel';
import { utcIsoDateToLocaleDateAndTimeAndDay } from '../../../../../utils/date';
import FileUpload from '../../../../../components/upload/FileUpload';
import { ConfigModel } from '../../../../../models/ConfigModel';

interface AuftragsresearchProps {
  selectedAuftrag: Auftrag | undefined;
  viewPDF: (fileName: number) => void;
  config: ConfigModel;
  loadDocument: (id: number) => void;
  dokumente: DokumentenModel[];
  onReload?: () => void;
  deleteFile: (dokumentverweisId: number, successHandler?: () => void) => void;
}

/**
 * AuftragSidebarTab-Component
 *
 * Dient zur Anzeige von Dokumenten und Ablieferbelegen
 *
 * @returns {JSX.Element} AuftragSidebarTab-Component
 * @constructor
 */
const AuftragDokumenteSidebarTab = (props: AuftragsresearchProps) => {
  useEffect(() => {
    props.selectedAuftrag?.dokumentenverweise?.forEach(verweis => {
      verweis.id && props.loadDocument(verweis.id);
    });
  }, [props.selectedAuftrag]);

  const view = () => {
    if (props.selectedAuftrag?.abliefernachweis?.startsWith('http')) {
      const a = document.createElement('a');
      a.setAttribute('hidden', '');
      a.setAttribute('href', props.selectedAuftrag?.abliefernachweis);
      a.setAttribute('target', '_blank');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      if (
        props.config.clientKunde?.toUpperCase() === 'BEXITY' &&
        props.selectedAuftrag?.abliefernachweis?.toUpperCase().includes('.PDF')
      ) {
        // im Auftrag steht 20220316_7087155300_75710413.PDF, es wird daraus https://asb.bexity.com/pdf/20220316/20220316_7087155300_75710413.PDF
        // und dann einfach Link öffnen

        const datum: string[] = props.selectedAuftrag?.abliefernachweis.split(
          '_',
          1
        );

        const a = document.createElement('a');
        a.setAttribute('hidden', '');
        a.setAttribute(
          'href',
          'https://asb.bexity.com/pdf/' +
            datum[0] +
            '/' +
            props.selectedAuftrag?.abliefernachweis
        );
        a.setAttribute('target', '_blank');
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        /*todo wenn das alte DPi bei Bexity abgeschaltet wurde, dann dieses hier entsprechend anpassen...
                  es muss dann in config_params unter auftrag.aln.url = https://asb.bexity.com/pdf/ eingetragen werden
                 und der Server braucht keinen Blob mehr schicken, es reicht die URL*/
        props.viewPDF(props.selectedAuftrag?.id ?? -1);
      }
    }
  };

  const viewSignatur = () => {
    return (
      <img
        className={'signatur'}
        src={`data:image/png;base64,${props.selectedAuftrag?.signatur}`}
      />
    );
  };

  const getMIMEType = (verweis: Dokumentenverweis): string => {
    let mimeType = 'application/octet-stream';
    const extension = verweis.dateiname
      ?.substr(
        verweis.dateiname?.lastIndexOf('.') + 1,
        verweis.dateiname?.length - 1
      )
      ?.toLowerCase();
    if (extension === 'jpg' || extension === 'png' || extension === 'jpeg')
      mimeType = 'image/' + extension;
    return mimeType;
  };

  const onDelete = (verweisId: number | undefined) => {
    verweisId && props.deleteFile(verweisId, props.onReload);
  };
  const canShowDeleteButton: boolean =
    !props.config.isDeleteDocumentsForbidden ||
    (props.selectedAuftrag?.status?.id ?? 0) < 3;

  const viewDocument = (verweis: Dokumentenverweis) => {
    return (
      <div key={verweis.id} className={'dokument'}>
        <div>Dokument: {verweis.dateiname}</div>
        <div>
          {`vom: ${
            verweis.datumBereitgestellt &&
            utcIsoDateToLocaleDateAndTimeAndDay(verweis.datumBereitgestellt)
          } `}
        </div>
        <br />
        <div className={'downloadContainer'}>
          <img
            className={'dokument-file'}
            src={`data:${getMIMEType(verweis)};base64,${
              props.dokumente.find(dokument => dokument.id === verweis.id)?.wert
            }`}
            alt={''}
          />
          <div className={'button-container'}>
            <a
              className={'document-button'}
              href={`data:${getMIMEType(verweis)};base64,${
                props.dokumente.find(dokument => dokument.id === verweis.id)
                  ?.wert
              }`}
              target='_blank'
              rel='noreferrer'
              download={verweis.dateiname}
              key={verweis.id}
            >
              <Button>
                Download &nbsp;
                <i className='fas fa-download'></i>
              </Button>
            </a>
            {canShowDeleteButton && (
              <Button
                onClickHandler={() => onDelete(verweis.id)}
                classes='document-button'
              >
                Delete &nbsp;
                <i className='fas fa-trash'></i>
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={'tab-content'}>
      <div className={'info-block'}>
        <p className={'fs-16-5'}>
          <strong>{props.selectedAuftrag?.spedeigennr ?? ''}</strong>
        </p>
      </div>
      {/*Ablieferbelege*/}
      {!!(
        props.selectedAuftrag?.abliefernachweisExists &&
        props.selectedAuftrag?.abliefernachweis
      ) && (
        <div className={'info-block'}>
          <label className={'info-label'}>{'Ablieferbeleg'}</label>
          <Button classes={'info-dokument'} onClickHandler={view}>
            {props.selectedAuftrag?.abliefernachweis
              ? props.selectedAuftrag.abliefernachweis.split(/[/\\]/).pop()
              : ''}
          </Button>
        </div>
      )}
      {/*Signatur*/}
      {!!props.selectedAuftrag?.signatur && (
        <div className={'info-block'}>
          <label className={'info-label'}>{'Signatur Zustellung'}</label>
          {viewSignatur()}
        </div>
      )}
      {/*Dokumentewnverweise*/}
      {props.config.dokumentenverweise && (
        <div className={'info-block'}>
          <label className={'info-label'}>{'Dokumente E-Akte'}</label>
          <FileUpload
            referenzId={props.selectedAuftrag?.id}
            onClickHandler={props.onReload}
          />
        </div>
      )}
      {!!props.selectedAuftrag?.dokumentenverweise?.length && (
        <div className={'info-block'}>
          {props.selectedAuftrag?.dokumentenverweise?.map(verweis =>
            viewDocument(verweis)
          )}
        </div>
      )}
    </div>
  );
};
const mapStateToProps = (state: StateModel) => ({
  config: state.Config,
  dokumente: state.Auftraege.dokumente,
});

export default connect(mapStateToProps, {
  viewPDF,
  loadDocument,
  deleteFile,
})(AuftragDokumenteSidebarTab);
